import { allows } from "@giftforward/entitlements";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import InternationalUpsellModal from "@/components/collections/InternationalUpsellModal";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import { openCampaignBuilderWithBudget } from "@/lib/campaigns";

type CollectionsContextValue = {
  /**
   * Country-gated "send this collection", shared by the tier page's slide-in CTA and the top-bar
   * "Send This Collection" button:
   *   - US → straight to the builder (Step 1, US) — free.
   *   - non-US + Pro+ → the builder with that country pre-selected.
   *   - non-US + not Pro+ → open the international upsell (no navigation).
   */
  sendCollection: (budget: number, country: string) => void;
};

const CollectionsContext = createContext<CollectionsContextValue | null>(null);

/**
 * Feature context for the Collections pages. Mounted ONCE around the app shell (like
 * `ImpactReportProvider` around Drawer) because its two consumers live in different components — the
 * tier page's slide-in CTA (in the routed page) and the top-bar "Send This Collection" button (in
 * Drawer's toolbar) — and it owns the single international upsell modal they share.
 */
export function CollectionsProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { ent } = useMyEntitlements();
  const canShipInternationally = !!ent && allows(ent, "campaign_send", "regions", "international");
  const [upsellOpen, setUpsellOpen] = useState(false);

  const sendCollection = useCallback(
    (budget: number, country: string) => {
      if (country === "US" || canShipInternationally) {
        openCampaignBuilderWithBudget(budget, navigate, country);
      } else {
        setUpsellOpen(true);
      }
    },
    [canShipInternationally, navigate],
  );

  const value = useMemo(() => ({ sendCollection }), [sendCollection]);

  return (
    <CollectionsContext.Provider value={value}>
      {children}
      <InternationalUpsellModal open={upsellOpen} onClose={() => setUpsellOpen(false)} />
    </CollectionsContext.Provider>
  );
}

export function useCollectionsContext(): CollectionsContextValue {
  const context = useContext(CollectionsContext);
  if (!context) {
    throw new Error("useCollectionsContext must be used within a <CollectionsProvider>");
  }
  return context;
}
